import { Event } from '../types/event.types';
import { AttendeeType } from '../types/enums';
import { api } from './client';

interface EventSearchFilters {
  search?: string;
  eventType?: string;
  status?: string;
  startDate?: string;
  hasAvailableSpots?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
}

interface EventSearchResponse {
  events: Event[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const eventService = {
  async getEvents(filters?: EventSearchFilters): Promise<EventSearchResponse> {
    try {
      const searchParams = new URLSearchParams();

      if (filters?.search) searchParams.append('search', filters.search);
      if (filters?.eventType) searchParams.append('filter.eventType', `$eq:${filters.eventType}`);
      if (filters?.status) searchParams.append('filter.status', `$eq:${filters.status}`);
      if (filters?.startDate) searchParams.append('filter.startDateTime', `$gte:${filters.startDate}`);
      if (filters?.hasAvailableSpots)
        searchParams.append('filter.hasAvailableSpots', `$eq:${filters.hasAvailableSpots}`);
      if (filters?.page) searchParams.append('page', filters.page.toString());
      if (filters?.limit) searchParams.append('limit', filters.limit.toString());
      if (filters?.sortBy) searchParams.append('sortBy', filters.sortBy);

      const queryString = searchParams.toString();
      const url = queryString ? `/member-events?${queryString}` : '/member-events';

      return await api.get<EventSearchResponse>(url);
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  async checkInAttendee(
    eventId: string,
    attendeeId: string,
    attendeeName?: string,
    checkedInByName?: string,
    attendeeType?: AttendeeType.ADULT | AttendeeType.CHILD,
    emergencyContactName?: string,
    emergencyContactPhone?: string,
  ) {
    try {
      const url = `/member-events/${eventId}/registrations/${attendeeId}/check-in`;
      const requestBody = {
        attendeeName,
        checkedInByName,
        attendeeType,
        emergencyContactName,
        emergencyContactPhone,
      };

      return await api.post(url, requestBody);
    } catch (error) {
      console.error('Error checking in attendee:', error);
      throw error;
    }
  },

  async checkOutAttendee(eventId: string, attendeeId: string) {
    try {
      const url = `/member-events/${eventId}/registrations/${attendeeId}/check-out`;
      const requestBody = {};

      return await api.post(url, requestBody);
    } catch (error) {
      console.error('Error checking out attendee:', error);
      throw error;
    }
  },

  /**
   * Get registration statuses for multiple attendees in a single batch request
   * Uses POST with request body to send attendee IDs
   */
  async getBatchRegistrationStatuses(
    eventId: string,
    attendeeIds: string[],
  ): Promise<Array<{ attendeeId: string; registrationStatus: string | null }>> {
    try {
      const url = `/member-events/${eventId}/registrations/statuses`;
      const response = await api.post<{
        statuses: Array<{ attendeeId: string; registrationStatus: string | null }>;
      }>(url, {
        attendeeIds,
      });

      return response.statuses || [];
    } catch (error) {
      console.error('Error fetching batch registration statuses:', error);
      // Return array with null statuses for all attendees
      return attendeeIds.map((attendeeId) => ({
        attendeeId,
        registrationStatus: null,
      }));
    }
  },

  /**
   * Get registration status for a specific attendee (legacy method, kept for backward compatibility)
   * @deprecated Use getBatchRegistrationStatuses instead for better performance
   */
  async getAttendeeRegistrationStatus(
    eventId: string,
    attendeeId: string,
  ): Promise<{ registrationStatus: string } | null> {
    try {
      const statuses = await this.getBatchRegistrationStatuses(eventId, [attendeeId]);
      if (statuses.length > 0 && statuses[0].registrationStatus) {
        return { registrationStatus: statuses[0].registrationStatus };
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Register an attendee for an event
   * Similar structure to backoffice RegistrationService.registerAttendee
   */
  async registerAttendee(
    eventId: string,
    data: {
      attendeeId: string;
      attendeeType: AttendeeType;
      registeredByPersonId?: string;
      attendeeName?: string;
      registeredByName?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
      specialNeedsNotes?: string;
      dietaryRestrictions?: string;
      needsTransportation?: boolean;
      needsMeals?: boolean;
      needsChildcare?: boolean;
    },
  ) {
    try {
      const url = `/member-events/${eventId}/registrations`;

      const requestBody = {
        attendeeId: data.attendeeId,
        attendeeType: data.attendeeType,
        registeredByPersonId: data.registeredByPersonId,
        attendeeName: data.attendeeName,
        registeredByName: data.registeredByName,
        emergencyContactName: data.emergencyContactName,
        emergencyContactPhone: data.emergencyContactPhone,
        specialNeedsNotes: data.specialNeedsNotes,
        dietaryRestrictions: data.dietaryRestrictions,
        needsTransportation: data.needsTransportation || false,
        needsMeals: data.needsMeals || false,
        needsChildcare: data.needsChildcare || false,
      };

      return await api.post(url, requestBody);
    } catch (error) {
      console.error('❌ Registration request failed:', error);
      throw error;
    }
  },

  /**
   * Register multiple attendees for an event (bulk registration)
   * Uses batch endpoint to register all attendees in a single API call
   */
  async registerMultipleAttendees(
    eventId: string,
    registrations: Array<{
      attendeeId: string;
      attendeeType: AttendeeType;
      registeredByPersonId?: string;
      attendeeName?: string;
      registeredByName?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
      specialNeedsNotes?: string;
      dietaryRestrictions?: string;
      needsTransportation?: boolean;
      needsMeals?: boolean;
      needsChildcare?: boolean;
    }>,
  ) {
    try {
      // Prepare request body for batch endpoint
      const requestBody = {
        registrations: registrations.map((registration) => ({
          attendeeId: registration.attendeeId,
          attendeeType: registration.attendeeType,
          registeredByPersonId: registration.registeredByPersonId,
          attendeeName: registration.attendeeName,
          registeredByName: registration.registeredByName,
          emergencyContactName: registration.emergencyContactName,
          emergencyContactPhone: registration.emergencyContactPhone,
          needsTransportation: registration.needsTransportation || false,
          needsMeals: registration.needsMeals || false,
          needsChildcare: registration.needsChildcare || false,
          specialNeedsNotes: registration.specialNeedsNotes,
          dietaryRestrictions: registration.dietaryRestrictions,
        })),
      };

      const response = await api.post<{
        results: Array<{
          attendeeId: string;
          success: boolean;
          eventAttendee?: {
            id: string;
            attendeeName: string;
            [key: string]: unknown;
          };
          error?: string;
        }>;
        succeeded: number;
        failed: number;
      }>(`/member-events/${eventId}/registrations`, requestBody);

      // Transform response to match expected format
      const successful: Array<{
        attendeeId: string;
        attendeeName: string;
        registrationId: string;
      }> = [];

      const failed: Array<{
        attendeeId: string;
        attendeeName: string;
        error: string;
      }> = [];

      response.results.forEach((result) => {
        if (result.success && result.eventAttendee) {
          successful.push({
            attendeeId: result.attendeeId,
            attendeeName: result.eventAttendee.attendeeName || result.attendeeId,
            registrationId: result.eventAttendee.id || 'unknown',
          });
        } else {
          const registration = registrations.find((r) => r.attendeeId === result.attendeeId);
          failed.push({
            attendeeId: result.attendeeId,
            attendeeName: registration?.attendeeName || result.attendeeId,
            error: result.error || 'Registration failed',
          });
        }
      });

      const result = {
        eventId,
        totalRegistered: successful.length,
        succeeded: successful.length,
        failedCount: failed.length,
        successful,
        failed,
      };

      return result;
    } catch (error) {
      console.error('❌ Bulk registration process failed:', error);
      throw error;
    }
  },
};
